import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ArrowLeft, Clock, MapPin, Calendar, Clock4, Ticket } from 'lucide-react';

export default function Cart() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [cartData, setCartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(240); // 4 minutes = 240 seconds
  const [isProcessing, setIsProcessing] = useState(false);

  const timerRef = useRef(null);

  const handleBackNavigation = () => {
    Swal.fire({
      title: 'Cancel Tickets?',
      text: 'Are you sure you want to cancel these tickets?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Cancel Tickets',
      cancelButtonText: 'No, Stay Here',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl font-bold px-5 py-2.5',
        cancelButton: 'rounded-xl font-bold px-5 py-2.5'
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        localStorage.removeItem('cart_timer_end');
        
        const currentUserStr = localStorage.getItem('user_data');
        const currentToken = localStorage.getItem('auth_token');
        const currentUser = user || (currentUserStr ? JSON.parse(currentUserStr) : null);
        const activeToken = token || currentToken;

        if (currentUser) {
          try {
            await fetch(`/api/clear_tmpcart/${currentUser.id}`, {
              headers: {
                'Authorization': `Bearer ${activeToken || ''}`,
                'Client-ID': 'lokanatya_client',
                'Client-Secret': 'super_secret_key_123'
              }
            });
          } catch (e) {
            console.error('Error clearing temp cart:', e);
          }
        }
        
        const showId = cartData?.event_id || cartData?.product_id || cartData?.gananatya?.id;
        if (showId) {
          navigate(`/shows/${showId}/seats`, { replace: true });
        } else {
          navigate('/shows', { replace: true });
        }
      }
    });
  };

  useEffect(() => {
    // Push dummy history entry to catch browser back button
    window.history.pushState({ inCart: true }, '', window.location.href);

    const handlePopState = () => {
      window.history.pushState({ inCart: true }, '', window.location.href);
      handleBackNavigation();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [cartData, user]);

  useEffect(() => {
    const tokenStr = localStorage.getItem('auth_token');
    const userDataStr = localStorage.getItem('user_data');
    if (tokenStr && userDataStr) {
      const u = JSON.parse(userDataStr);
      setUser(u);
      setToken(tokenStr);
      fetchCartData(u.id, tokenStr);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Timer logic
    const savedEndTime = localStorage.getItem('cart_timer_end');
    let endTime;

    if (savedEndTime && cartData) {
      endTime = parseInt(savedEndTime, 10);
      const now = Math.floor(Date.now() / 1000);
      if (endTime > now) {
        setTimeLeft(endTime - now);
      } else {
        handleTimeout();
        return;
      }
    } else if (cartData) {
      const now = Math.floor(Date.now() / 1000);
      endTime = now + 240;
      localStorage.setItem('cart_timer_end', endTime.toString());
      setTimeLeft(240);
    }

    if (cartData) {
      timerRef.current = setInterval(() => {
        const now = Math.floor(Date.now() / 1000);
        const remaining = endTime - now;

        if (remaining <= 0) {
          clearInterval(timerRef.current);
          handleTimeout();
        } else {
          setTimeLeft(remaining);
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [cartData]);

  const fetchCartData = async (userId, userToken) => {
    try {
      const res = await fetch(`/api/fetch_from_temp/${userId}`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Accept': 'application/json',
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        }
      });
      const data = await res.json();

      if (data.success && data.temp_tickets && data.temp_tickets.length > 0) {
        setCartData(data.temp_tickets[0]); // Displaying first event for now
      } else {
        setCartData(null);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeout = async () => {
    localStorage.removeItem('cart_timer_end');
    if (user) {
      try {
        await fetch(`/api/clear_tmpcart/${user.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Client-ID': 'lokanatya_client',
            'Client-Secret': 'super_secret_key_123'
          }
        });
      } catch (e) {
        console.error(e);
      }
    }
    Swal.fire({
      title: 'Time Expired!',
      text: 'Your ticket has been cancelled due to timeout.',
      icon: 'error',
      confirmButtonText: 'OK',
      allowOutsideClick: false
    }).then(() => {
      navigate(-1);
    });
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };


  const handlePayment = async () => {
    if (!cartData || !user) return;
    setIsProcessing(true);

    const totalAmount = cartData.tickets.reduce((acc, curr) => acc + parseFloat(curr.seat_price), 0);
    const platformFee = 0.00; // Hardcoded UI fee or from API
    const finalAmount = totalAmount + platformFee;

    try {
      // 1. Load Razorpay script
      const res = await loadRazorpay();
      if (!res) {
        Swal.fire('Error', 'Razorpay SDK failed to load', 'error');
        setIsProcessing(false);
        return;
      }

      // 2. Create Order
      const orderRes = await fetch('/api/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        },
        body: JSON.stringify({ total_amount: finalAmount })
      });
      const orderData = await orderRes.json();

      if (!orderData.success) {
        Swal.fire('Error', 'Failed to create order', 'error');
        setIsProcessing(false);
        return;
      }

      // 3. Setup Razorpay options
      const options = {
        key: 'rzp_live_R6pWMaNGHKnbcZ', // Matching backend RAZORPAY_KEY
        amount: Math.round(finalAmount * 100),
        currency: 'INR',
        name: cartData.gananatya.jatra_name,
        description: 'Ticket Booking',
        image: 'https://lokanatya.com/logo.png', // Corrected Logo URL
        order_id: orderData.order_id,
        handler: async function (response) {
          // 4. Verify Payment
          try {
            const verifyRes = await fetch('/api/verify-payment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Client-ID': 'lokanatya_client',
                'Client-Secret': 'super_secret_key_123'
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                product_id: cartData.gananatya.id,
                seats: cartData.tickets.map(t => t.seat_id),
                user_id: user.id,
                total_amount: finalAmount,
                seat_names: cartData.tickets.map(t => t.seat_name).join(', ')
              })
            });
            const verifyData = await verifyRes.json();

            if (verifyData.success) {
              localStorage.removeItem('cart_timer_end');
              clearInterval(timerRef.current);
              Swal.fire('Success', 'Payment Successful!', 'success').then(() => {
                navigate('/my-tickets'); // Redirect to my-tickets instead of tickets (since my-tickets is the route in Navbar)
              });
            } else {
              Swal.fire('Error', 'Payment verification failed', 'error');
            }
          } catch (err) {
            Swal.fire('Error', 'Error during verification', 'error');
          }
        },
        prefill: {
          name: user.name || 'User',
          email: user.email || 'user@example.com',
          contact: user.mobile || '9999999999'
        },
        theme: {
          color: '#19479a' // Match the exact blue theme
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Something went wrong during payment', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    if (!isLoading && !cartData) {
      navigate('/', { replace: true });
    }
  }, [isLoading, cartData, navigate]);

  if (isLoading || !cartData) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-red-600"></div>
      </div>
    );
  }

  const jatra = cartData.gananatya;
  const tickets = cartData.tickets;
  const totalTicketPrice = tickets.reduce((acc, curr) => acc + parseFloat(curr.seat_price), 0);
  const platformFee = 0.00;
  const grandTotal = totalTicketPrice + platformFee;

  return (
    <div className="min-h-screen bg-gray-50 pb-2 lg:pb-12" style={{ fontFamily: "'Nunito', sans-serif" }}>
      {/* Header */}
      <div className="bg-white sticky top-0 z-40 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handleBackNavigation} className="w-8 h-8 border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50 text-gray-600 transition shrink-0" title="Cancel & Go Back">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-lg md:text-xl font-black text-gray-900 leading-tight">Confirm Booking</h1>
              <p className="text-[10px] md:text-xs text-gray-500 font-medium hidden md:block">Review your booking details and proceed to payment</p>
            </div>
          </div>
          {/* Timer Inline */}
          <div className="text-right shrink-0">
            <span className="text-[9px] md:text-[10px] text-gray-500 font-bold uppercase tracking-wider block mb-0.5">Expires in</span>
            <span className="text-sm md:text-base font-black text-red-600 leading-none">{formatTime(timeLeft)}</span>
          </div>
        </div>
        {/* Time bar (progress bar) */}
        <div className="h-1 bg-gray-100 w-full relative">
          <div
            className="absolute top-0 left-0 h-full bg-red-500 transition-all duration-1000 ease-linear"
            style={{ width: `${(timeLeft / 240) * 100}%` }}
          ></div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative">
          {/* Left Column (Main Ticket) */}
          <div className="lg:col-span-7 xl:col-span-8 h-full">
            <div className="bg-white rounded-[15px] shadow-sm border border-gray-200 p-2 md:p-4 flex flex-col md:flex-row gap-2 md:gap-6 hover:shadow-md transition-shadow h-full">
              {/* Poster Image */}
              <div className="w-full h-[200px] md:h-auto md:w-[280px] shrink-0 md:aspect-[3/4] rounded-[10px] overflow-hidden relative bg-gray-100 shadow-inner">
                <img
                  src={jatra.image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"}
                  alt={jatra.jatra_name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg" }}
                />
                {/* Red Gradient Tag on Image */}
                <div className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg max-w-[90%] truncate border border-red-400/50">
                  {jatra.gananatya_name}
                </div>
              </div>

              {/* Details */}
              <div className="flex-1 pt-2 pb-4 px-2 md:py-4 md:px-0 md:pr-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="bg-red-50 text-red-600 border border-red-100 font-bold text-[10px] uppercase px-3 py-1 rounded-md tracking-wider">OPERA</span>
                  <span className="bg-gray-100 text-gray-600 font-bold text-[10px] uppercase px-3 py-1 rounded-md tracking-wider">{tickets.length} Seat{tickets.length > 1 ? 's' : ''}</span>
                </div>

                <h2 className="text-lg md:text-xl font-black text-gray-900 leading-tight mb-2">{jatra.jatra_name}</h2>
                <p className="text-xs md:text-sm font-bold text-gray-500 mb-8 pb-6 border-b border-gray-100">Presented by <span className="text-gray-900">{jatra.gananatya_name}</span></p>

                <div className="space-y-5 text-left">
                  <div className="flex flex-row items-center gap-8">
                    <div className="flex gap-2.5 items-center">
                      <Calendar size={16} className="text-[#e33535] shrink-0" />
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-[1px]">Show Date</p>
                        <p className="text-xs font-bold text-gray-900 tracking-tight">{jatra.jatra_date}</p>
                      </div>
                    </div>
                    <div className="flex gap-2.5 items-center">
                      <Clock size={16} className="text-[#e33535] shrink-0" />
                      <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-[1px]">Show Time</p>
                        <p className="text-xs font-bold text-gray-900 tracking-tight">{jatra.jatra_time || '11:00 pm'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <MapPin size={20} className="text-[#e33535] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">Location</p>
                      <p className="text-sm font-bold text-gray-900 mb-0.5">{jatra.jatra_location}</p>
                      <p className="text-sm text-gray-500 font-medium">Bhubaneswar, Odisha</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <Ticket size={20} className="text-[#e33535] shrink-0 mt-0.5 transform -rotate-45" />
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">Your Seats</p>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {tickets.map((t, idx) => (
                          <span key={t.id || idx} className="bg-red-50 border border-red-300 text-red-600 text-[11px] font-black px-3 py-1 rounded-full shadow-sm">
                            {t.seat_name || t.seat_number}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column (Checkout Tools) */}
          <div className="lg:col-span-5 xl:col-span-4 h-full">
            {/* Price Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 hover:shadow-md transition-shadow h-full flex flex-col">
              <div className="flex items-center gap-2 mb-6 text-gray-900 font-black text-lg">
                Price Breakdown <span className="text-gray-400 ml-1 text-sm cursor-help">ⓘ</span>
              </div>

              <div className="space-y-4 text-sm mb-6 border-b border-dashed border-gray-200 pb-6 text-left">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Base fare ({tickets.length} seat{tickets.length > 1 ? 's' : ''})</span>
                  <span className="font-bold text-gray-900">₹{totalTicketPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Convenience fee</span>
                  <span className="font-bold text-gray-900">₹{platformFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">GST on convenience fee</span>
                  <span className="font-bold text-gray-900">₹0.00</span>
                </div>
              </div>

              <div className="flex flex-col items-end justify-end mb-8 mt-auto pt-4 hidden lg:flex">
                <p className="text-[11px] font-bold text-gray-500 mb-1.5 tracking-widest uppercase">Amount Payable</p>
                <p className="text-4xl font-black text-gray-900 leading-none">₹{grandTotal.toFixed(2)}</p>
              </div>
              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md font-black shadow-lg shadow-red-600/20 transition-all active:scale-95 disabled:opacity-70 disabled:shadow-none hidden lg:flex items-center justify-center gap-2"
              >
                {isProcessing ? 'Processing...' : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    Confirm Booking
                  </>
                )}
              </button>

              <div className="text-center flex items-center justify-center gap-1.5 text-[9px] text-gray-500 font-bold uppercase tracking-widest mt-4">
                <span className="text-green-500 text-xs">✔</span> Secured by Cashfree Payments • All taxes inclusive
              </div>
            </div>
          </div>
        </div>

        {/* Info Grid */}
        <div className="mt-6 md:mt-8 bg-white border border-gray-200 rounded-xl md:rounded-2xl p-4 md:p-8 shadow-sm">
          <div className="grid grid-cols-5 divide-x divide-gray-100">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3 border border-red-100"><span className="text-lg">⚡</span></div>
              <p className="text-xs font-black text-gray-900 leading-tight">Instant Confirm</p>
              <p className="text-[9px] text-gray-500 font-medium mt-1 hidden md:block">Get confirmation instantly</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3 border border-red-100"><span className="text-lg">↩️</span></div>
              <p className="text-xs font-black text-gray-900 leading-tight">Easy Refunds</p>
              <p className="text-[9px] text-gray-500 font-medium mt-1 hidden md:block">Hassle-free refunds</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3 border border-red-100"><span className="text-lg">⭐</span></div>
              <p className="text-xs font-black text-gray-900 leading-tight">Official Only</p>
              <p className="text-[9px] text-gray-500 font-medium mt-1 hidden md:block">Authorized ticket partner</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3 border border-red-100"><span className="text-lg">📱</span></div>
              <p className="text-xs font-black text-gray-900 leading-tight">Digital Ticket</p>
              <p className="text-[9px] text-gray-500 font-medium mt-1 hidden md:block">Show ticket on phone</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3 border border-red-100"><span className="text-lg">💯</span></div>
              <p className="text-xs font-black text-gray-900 leading-tight">100% Genuine</p>
              <p className="text-[9px] text-gray-500 font-medium mt-1 hidden md:block">Authentic & verified</p>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Action Bar (Mobile Only - Desktop uses right column button) */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 shadow-[0_-10px_40px_rgba(0,0,0,0.08)] z-50">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div>
              <p className="text-[10px] text-gray-500 font-bold mb-0.5 uppercase tracking-wider">Amount Payable</p>
              <p className="text-xl font-black text-gray-900 leading-none">₹{grandTotal.toFixed(2)}</p>
            </div>

            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className="bg-red-600 text-white px-6 py-2.5 rounded-lg font-black text-sm shadow-lg shadow-red-600/20 flex items-center gap-2 hover:bg-red-700 transition active:scale-95 disabled:opacity-70 disabled:shadow-none"
            >
              {isProcessing ? 'Processing...' : 'PAY NOW'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
